import {
  Alert,
  Flex,
  Heading,
  IconCertifiedLine,
  IconStudentViewLine,
  List,
  Text,
  View,
} from "@instructure/ui";
import { t } from "i18next";
import { useContext, useEffect, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useSearchParams } from "react-router-dom";
import {
  ERROR,
  useNotification,
} from "../../../components/CustomHooks/NotificationProvider";
import { OfferingService } from "../../../services/OfferingService";
import { SectionService } from "../../../services/SectionService";
import { UserDetailContext } from "../../../utils/Context";
import { default as colors } from "../../../utils/CourseColors";
import SectionCard from "./Components/SectionCard";
import AssignmentView from "./Views/AssignmentView";
import CourseView from "./Views/CourseView";

const FacultyDashboard = ({ trigger, setTrigger, subjects }) => {
  const userDetails = useContext(UserDetailContext);
  const showNotification = useNotification();
  const [searchParams] = useSearchParams();
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("course");
  const [selectedAssignment, setSelectedAssignment] = useState({});
  const [chartLoad, setChartLoad] = useState(false);
  const selectedCourseRef = useRef(null);

  const [facDashboardFilters, setFacDashboardFilters] = useState({
    subject: "",
    LOType: "CSLO",
    programs: [],
    categories: [],
    terms: [],
    masteryResults: "true",
    cycle: {},
    courseLevels: [],
    courses: [],
    type: [],
    variety: ["outcome"],
    offerings: [],
  });
  const [noSections, setNoSections] = useState(false);

  let courseIdFilter = searchParams.get("courseId");

  useEffect(() => {
    if (!loading && courseIdFilter && selectedCourseRef?.current) {
      selectedCourseRef.current.ref.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start",
      });
    }
  }, [loading, courseIdFilter]);

  useEffect(() => {
    const fetchSectionInfo = async () => {
      try {
        const [offerings, facultySections] = await Promise.all([
          OfferingService.getOfferingsByFacultyID(userDetails.canvas_id),
          SectionService.getSectionsByFacultyID(userDetails.canvas_id),
        ]);

        if (facultySections && facultySections.length > 0) {
          let selectedSection = facultySections[0];
          facultySections.forEach((section) => {
            const offering = offerings.find(
              (offering) => offering.id === section.offering.id
            );
            if (offering) {
              section.course_id = offering.course_summary.parent_course_id;
              section.course_code = offering.course_summary.code;
              section.learning_outcomes =
                offering && Object.hasOwn(offering, "learning_outcomes")
                  ? offering.learning_outcomes.filter(
                      (lo) => !Object.hasOwn(lo, "unapproved")
                    )
                  : [];
              section.unapproved_outcomes =
                offering && Object.hasOwn(offering, "learning_outcomes")
                  ? offering.learning_outcomes.filter(
                      (lo) => Object.hasOwn(lo, "unapproved") && lo.unapproved
                    )
                  : [];
              section.current = false;
              if (section.name) {
                section.name = section.name.replace(/,/g, ", ");
              }
              // values are of a different type (int and str) hence the loose equality operator (==) is intentional
              if (section.canvas_id == courseIdFilter) {
                selectedSection = section;
              }
            }
          });
          selectedSection.current = true;
          setSections(facultySections);
          setFacDashboardFilters((prevFilters) => ({
            ...prevFilters,
            subject: facultySections[0].offering?.subject
              ? subjects.find(
                  (s) => s.name === facultySections[0].offering.subject
                )?.title
              : t("dashboard.noDepartmentAvailable"),
            courses: [], // default to populating the offering results on fac dashboard
            offerings: selectedSection.offering?.id
              ? [selectedSection.offering.id]
              : [],
            LOType: "CSLO",
            masteryResults: "true",
          }));
          setLoading(false);
        } else {
          setNoSections(true);
        }
      } catch {
        showNotification(
          t("notifications.general.fetchError", { obj: "Course Information" }),
          ERROR
        );
      }
    };
    if (userDetails.name && subjects.length > 0) {
      fetchSectionInfo();
    }
  }, [userDetails, subjects, courseIdFilter, showNotification]);

  const detailTitle = () => {
    const currentSection = sections.find((c) => c.current);
    let title = t("dashboard.detailsFor") + ": ";
    title +=
      currentSection.name ??
      currentSection.offering.code + ", " + currentSection.term.name;

    if (selectedAssignment.assignment) {
      title +=
        " > " +
        (selectedAssignment.assignment.course_id === -1
          ? t("assessment.externalAssessment")
          : selectedAssignment.assignment.name);
    }

    return title;
  };

  return (
    <Flex alignItems="start" gap="medium">
      <Flex.Item size="350px">
        <Heading as="h2" level="h3" border="bottom">
          {t("navigation.courses")}
        </Heading>
        <Flex wrap="wrap" margin="small 0 0 0">
          <Flex.Item size="100%">
            <IconStudentViewLine />{" "}
            <Text weight="bold">: {t("dashboard.currentView")}</Text>
          </Flex.Item>
          <Flex.Item>
            <IconCertifiedLine />{" "}
            <Text weight="bold">: {t("dashboard.plannedAssignment")}</Text>
          </Flex.Item>
        </Flex>
        <div
          style={{ maxHeight: 800, overflowY: "auto", padding: "0 15px 0 5px" }}
        >
          {!noSections &&
            (!loading ? (
              <List isUnstyled>
                {sections.map((section, index) => (
                  <List.Item
                    margin="small 0"
                    key={index}
                    ref={section.current ? selectedCourseRef : null}
                  >
                    <SectionCard
                      section={section}
                      setSections={setSections}
                      cardColor={colors[index]}
                      setView={setView}
                      selectedAssignment={selectedAssignment}
                      setSelectedAssignment={setSelectedAssignment}
                      setDashboardFilters={setFacDashboardFilters}
                      setTrigger={setTrigger}
                      setChartLoad={setChartLoad}
                      subjects={subjects}
                    />
                  </List.Item>
                ))}
              </List>
            ) : (
              <Skeleton height={128} count={5} style={{ margin: "0.5rem 0" }} />
            ))}
        </div>
      </Flex.Item>
      <Flex.Item shouldGrow shouldShrink>
        <View as="div" minWidth={900}>
          <Heading as="h1" level="h3" border="bottom" margin="0 0 small 0">
            {noSections
              ? t("dashboard.noDataToDisplay")
              : !loading
                ? detailTitle()
                : t("global.loading") + "..."}
          </Heading>
          {noSections ? (
            <Alert variant="info">{t("dashboard.noSections")}</Alert>
          ) : !loading ? (
            view === "course" ? (
              <CourseView
                currentSection={sections.find((c) => c.current)}
                dashboardFilters={facDashboardFilters}
                setDashboardFilters={setFacDashboardFilters}
                trigger={trigger}
                setTrigger={setTrigger}
                chartLoad={chartLoad}
                setChartLoad={setChartLoad}
              />
            ) : (
              <AssignmentView
                currentSection={sections.find((c) => c.current)}
                dashboardFilters={facDashboardFilters}
                setDashboardFilters={setFacDashboardFilters}
                trigger={trigger}
                setTrigger={setTrigger}
                selectedAssignment={selectedAssignment}
                chartLoad={chartLoad}
                setChartLoad={setChartLoad}
              />
            )
          ) : (
            <Skeleton height={330} count={2} style={{ margin: "10px 0" }} />
          )}
        </View>
      </Flex.Item>
    </Flex>
  );
};

export default FacultyDashboard;
